/**
 * PushReentryReport — /#/reports/push-reentry
 *
 * Deals re-entered after PUSH_TO_NEXT_QUARTER. Shows which CPs were
 * re-validated, which were retained, and whether the audit log shows a
 * quarter-rollover trigger event.
 */
import { useEffect, useMemo, useState } from "react";
import ReportShell, { thStyle, tdStyle, BLACK, RED, SLATE, BORDER, EmptyState, LoadingState } from "./_ReportShell";
import { PushReentryRow, downloadCsv, listPushReentryRows, toCsv } from "@/lib/reports-saturday";

export default function PushReentryReport() {
  const [rows, setRows] = useState<PushReentryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await listPushReentryRows();
        if (!cancelled) setRows(r);
      } catch (err: any) {
        console.error("[PushReentryReport]", err);
        if (!cancelled) setError(err?.message ?? "Failed to load");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const csv = useMemo(() => {
    if (!rows) return "";
    return toCsv(rows.map((r) => ({
      account_name: r.account_name ?? "",
      reentered_in_quarter: r.reentered_in_quarter ?? "",
      reentered_at: r.reentered_at ?? "",
      revalidated_cps: r.revalidated_cps.join("|"),
      retained_cps: r.retained_cps.join("|"),
      all_forced_cps_cleared: r.all_forced_cps_cleared ? "yes" : "no",
      quarter_rollover_triggered: r.quarter_rollover_triggered ? "yes" : "no",
    })));
  }, [rows]);

  return (
    <ReportShell
      title="PUSH Re-entry"
      subtitle="Deals re-entered after PUSH_TO_NEXT_QUARTER, with revalidated CPs and rollover triggers."
      rightLabel="PUSH Re-entry"
      onExport={rows && rows.length > 0 ? () => downloadCsv("push-reentry.csv", csv) : undefined}
    >
      {error ? (
        <EmptyState message={`Could not load: ${error}`} />
      ) : !rows ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState message="No re-entered deals on record. Data activates as deals roll into the next quarter." />
      ) : (
        <div style={{ overflowX: "auto", border: `1px solid ${BORDER}`, borderRadius: 8, background: "#FFFFFF" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead>
              <tr>
                <th style={thStyle}>Account</th>
                <th style={thStyle}>Re-entered quarter</th>
                <th style={thStyle}>Re-entered at</th>
                <th style={thStyle}>Re-validated CPs</th>
                <th style={thStyle}>Retained CPs</th>
                <th style={thStyle}>All forced CPs cleared</th>
                <th style={thStyle}>Rollover triggered</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.deal_id}>
                  <td style={tdStyle}>{r.account_name ?? "—"}</td>
                  <td style={tdStyle}>{r.reentered_in_quarter ?? "—"}</td>
                  <td style={tdStyle}>{r.reentered_at ?? "—"}</td>
                  <td style={tdStyle}>
                    {r.revalidated_cps.length === 0 ? <span style={{ color: SLATE }}>—</span> : r.revalidated_cps.map((c) => (
                      <span key={c} style={{ marginRight: 6, padding: "2px 6px", borderRadius: 4, background: "#DCFCE7", color: "#166534", fontSize: 11, fontWeight: 700 }}>CP{c}</span>
                    ))}
                  </td>
                  <td style={tdStyle}>
                    {r.retained_cps.length === 0 ? <span style={{ color: SLATE }}>—</span> : r.retained_cps.map((c) => (
                      <span key={c} style={{ marginRight: 6, padding: "2px 6px", borderRadius: 4, background: "#FEF3C7", color: "#92400E", fontSize: 11, fontWeight: 700 }}>CP{c}</span>
                    ))}
                  </td>
                  <td style={{ ...tdStyle, color: r.all_forced_cps_cleared ? "#16A34A" : RED, fontWeight: 700 }}>
                    {r.all_forced_cps_cleared ? "Yes" : "No"}
                  </td>
                  <td style={tdStyle}>{r.quarter_rollover_triggered ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ReportShell>
  );
}
